'use client'

import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Hash } from 'lucide-react'
import { DemoFrame } from './demoKit'
import { useOnScreen, useSequence } from './demoHooks'
import { ResultStamp, Trail } from './demoParts'

// GHL experts for agencies: what the page says working together looks like:
// a shared Slack channel, a dedicated specialist who replies the same day in
// plain English, and everything delivered under the agency's brand. Two ways
// agencies use the team: a few hours of overflow, or a full GHL department.
// The specialist has no personal name, matching how the team is shown.

const MODES = {
  overflow: {
    label: 'Overflow work',
    messages: [
      { who: 'you', text: 'Bayside Dental needs a missed-call text-back and review requests by Friday.' },
      { who: 'us', text: "On it. I'll build both in their sub-account today and send you a short walkthrough video.", meta: 'Replied in 18 min' },
      { who: 'us', text: 'Ready for your review. All under your brand, no mention of us anywhere.' },
      { who: 'you', text: 'Approved, sending it to the client now.' },
    ],
    side: { title: 'This month', items: [
      { at: 2, text: 'Missed-call text-back built' },
      { at: 3, text: 'Review request flow built' },
      { at: 4, text: '5 hours used, no contract' },
    ] },
    result: { tone: 'good', text: 'Delivered in 2 days · your brand' },
  },
  department: {
    label: 'Your full GHL department',
    messages: [
      { who: 'you', text: 'We signed 12 new clients this month. Can you take all of the onboarding?' },
      { who: 'us', text: 'Yes. Setup, A2P and automations for all 12, plus support for their users. The first four go live Wednesday.', meta: 'Replied in 25 min' },
      { who: 'us', text: '8 of 12 are live. The other four are waiting on logins, so I am chasing them today.' },
      { who: 'you', text: 'Perfect. No new hires needed.' },
    ],
    side: { title: 'Onboarding board', items: [
      { at: 2, text: 'Accounts and A2P for 12 clients' },
      { at: 3, text: '8 clients live, 4 in progress' },
      { at: 4, text: 'User support handled under your brand' },
    ] },
    result: { tone: 'good', text: '12 clients onboarded · no new hires' },
  },
}
const DELAYS = [500, 1500, 1500, 1200, 700]

export default function AgencyChannel() {
  const [ref, onScreen] = useOnScreen()
  const [mode, setMode] = useState('overflow')
  const [run, setRun] = useState(0)
  const data = MODES[mode]
  const step = useSequence(DELAYS, { run: onScreen, resetKey: `${mode}-${run}` })
  const finished = step >= DELAYS.length
  const shown = data.messages.slice(0, Math.min(step, data.messages.length))
  const pick = (key) => { setMode(key); setRun((n) => n + 1) }

  return (
    <DemoFrame
      frameRef={ref}
      className="svd-channel"
      title="Your shared channel with GHL Prime"
      label="Demo: an agency working with its GHL Prime specialist in a shared Slack channel"
      tabs={Object.entries(MODES).map(([key, v]) => ({ key, label: v.label }))}
      activeTab={mode}
      onTab={pick}
      onReplay={() => pick(mode)}
    >
      <div className="svd-agent-grid">
        <div className="svd-slack">
          <div className="svd-slack-head"><Hash size={14} aria-hidden="true" /> yourbrand-ghl <small>shared with GHL Prime</small></div>
          <div className="svd-slack-log" aria-live="polite">
            <AnimatePresence initial={false}>
              {shown.map((m, i) => (
                <motion.div key={`${mode}-${run}-${i}`} className={`svd-slack-msg is-${m.who}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <span className="svd-slack-avatar" aria-hidden="true">{m.who === 'us' ? 'GP' : 'You'}</span>
                  <span>
                    <b>{m.who === 'us' ? 'Your GHL Prime specialist' : 'You (agency owner)'}</b>
                    {m.meta ? <small>{m.meta}</small> : null}
                    <p>{m.text}</p>
                  </span>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
        <Trail title={data.side.title} items={data.side.items} step={step} />
      </div>
      <ResultStamp show={finished} tone={data.result.tone} text={data.result.text} id={`${mode}-${run}`} />
    </DemoFrame>
  )
}
